import React from 'react';

import { defineMessage, useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import LozengeIcon from '@atlaskit/icon-lab/core/lozenge';
import Lozenge from '@atlaskit/lozenge/lozenge';
import { Box, Flex, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { type StatusCategory, type UpdateStatusRovoPayload } from '@atlaskit/rovo-triggers/types';
import { token } from '@atlaskit/tokens';

const updateStatusNameHeading = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.jira-workflow-wizard-actions.UpdateStatusAction.updateStatusNameHeading',
	defaultMessage: 'Update status name',
	description:
		'Heading for the section of a Jira workflow builder action card that shows the new name a status will be renamed to.',
});

const statusCategoryHeading = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.jira-workflow-wizard-actions.UpdateStatusAction.statusCategoryHeading',
	defaultMessage: 'Status category',
	description:
		'Heading for the section of a Jira workflow builder action card that shows the category a status will be moved to.',
});

const statusCategoryTodo = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.jira-workflow-wizard-actions.UpdateStatusAction.statusCategoryTodo',
	defaultMessage: 'To do',
	description: 'Name of the Jira status category for work that has not been started.',
});

const statusCategoryInProgress = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.jira-workflow-wizard-actions.UpdateStatusAction.statusCategoryInProgress',
	defaultMessage: 'In progress',
	description: 'Name of the Jira status category for work that is underway.',
});

const statusCategoryDone = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.jira-workflow-wizard-actions.UpdateStatusAction.statusCategoryDone',
	defaultMessage: 'Done',
	description: 'Name of the Jira status category for work that is complete.',
});

const statusCategoryUndefined = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.jira-workflow-wizard-actions.UpdateStatusAction.statusCategoryUndefined',
	defaultMessage: 'None',
	description: 'Shown when a Jira status has no category assigned.',
});

const STATUS_CATEGORY_TO_LOZENGE_APPEARANCE = {
	UNDEFINED: 'neutral',
	TODO: 'neutral',
	DONE: 'success',
	IN_PROGRESS: 'information',
} as const;

const STATUS_CATEGORY_TO_LABEL = {
	UNDEFINED: statusCategoryUndefined,
	TODO: statusCategoryTodo,
	DONE: statusCategoryDone,
	IN_PROGRESS: statusCategoryInProgress,
} as const;

const styles = cssMap({
	statusIcon: {
		width: '24px',
		height: '24px',
		backgroundColor: token('color.background.accent.gray.subtlest'),
		borderRadius: token('radius.small'),
		color: token('color.text.accent.gray'),
		flexShrink: 0,
	},
	card: {
		width: '100%',
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		borderStyle: 'solid',
		borderRadius: token('radius.small'),
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		backgroundColor: token('elevation.surface.raised'),
	},
	swatch: {
		width: '16px',
		height: '16px',
		borderRadius: token('radius.small'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		flexShrink: 0,
	},
	UNDEFINED: {
		borderColor: token('color.border.bold'),
		backgroundColor: token('color.background.neutral'),
	},
	TODO: {
		borderColor: token('color.border.bold'),
		backgroundColor: token('color.background.neutral'),
	},
	IN_PROGRESS: {
		borderColor: token('color.border.brand'),
		backgroundColor: token('color.background.selected'),
	},
	DONE: {
		borderColor: token('color.border.success'),
		backgroundColor: token('color.background.success'),
	},
});

export const UpdateStatusAction = ({
	fromStatusCategory,
	toStatusName,
	toStatusCategory,
}: UpdateStatusRovoPayload): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	if (!toStatusName && !toStatusCategory) {
		return null;
	}

	const lozengeCategory: StatusCategory = toStatusCategory ?? fromStatusCategory ?? 'UNDEFINED';

	return (
		<Stack space="space.100">
			{toStatusName && (
				<Stack space="space.100">
					<Heading size="xsmall">{formatMessage(updateStatusNameHeading)}</Heading>
					<Inline xcss={styles.card} alignBlock="center" space="space.150">
						<Flex xcss={styles.statusIcon} justifyContent="center" alignItems="center">
							<LozengeIcon label="" />
						</Flex>
						<Lozenge appearance={STATUS_CATEGORY_TO_LOZENGE_APPEARANCE[lozengeCategory]}>
							{toStatusName}
						</Lozenge>
					</Inline>
				</Stack>
			)}

			{toStatusCategory && (
				<Stack space="space.100">
					<Heading size="xsmall">{formatMessage(statusCategoryHeading)}</Heading>
					<Inline xcss={styles.card} alignBlock="center" space="space.150">
						<Box xcss={cx(styles.swatch, styles[toStatusCategory])} />
						<Text>{formatMessage(STATUS_CATEGORY_TO_LABEL[toStatusCategory])}</Text>
					</Inline>
				</Stack>
			)}
		</Stack>
	);
};
